import { useRef, useState } from 'react'

import type { Media } from '../../api/types'
import { dauerText, kannAbspielen } from '../../lib/video'
import { groesse } from '../../lib/format'
import { notiere } from '../../lib/sendeprotokoll'
import { formatName } from '../../lib/videowandeln'
import { useTexte } from '../../texte'

/**
 * Ein Video im Verlauf.
 *
 * Zwei Fälle, und der zweite ist der Grund, warum es diese Datei gibt:
 *
 * 1. **Der Browser kann den Typ.** Dann steht hier ein Player mit Standbild.
 *    Wie viel er im Voraus holt, steht am `preload` unten und ist dort
 *    begründet.
 * 2. **Der Browser kann den Typ nicht.** Ein iPhone nimmt in HEVC auf; Chrome
 *    und Firefox spielen das nicht ab. Statt eines Players, der schwarz
 *    bleibt und keine Erklärung gibt, steht dann das Standbild da, ein Satz
 *    dazu und ein Weg zur Datei.
 *
 * Umgewandelt wird nichts: ohne ffmpeg kann der Server es nicht, und im
 * Browser dauerte es Minuten je Video.
 *
 * **Und ein dritter Fall, seit 1.5.0: der Player meldet einen Fehler.** Aus
 * dem Betrieb, am iPad: drei 4K-Videos in einem Verlauf standen erst mit
 * Standbild da und spielten, dann waren die Standbilder weg, die Kacheln
 * schwarz und eine zeigte „Fehler" -- Safaris eigene Beschriftung, die App
 * hatte keine. Der Server hatte die Dateien noch (*Medien › Abgleichen*: null
 * Einträge ohne Datei), und nach dem Neuladen spielten sie wieder. Der Fehler
 * entsteht also im Gerät, siehe `preload` unten. Was hier dazukam: der Fehler
 * wird gesehen (`onError`), er steht in der Diagnose, das Standbild bleibt
 * stehen, und ein Knopf lädt neu.
 */
/**
 * **Kein Antwortknopf mehr am Video.**
 *
 * Hier stand „Auf dieses Video antworten" als Pille über dem Player. Aus dem
 * Betrieb: „außerdem kann dann bei Videos der Knopf ‚auf dieses Video
 * antworten' entfallen" -- seit das Menü beim langen Drücken (#8) ein
 * *Antworten* trägt, war er ein zweiter Weg zu derselben Sache, und einer,
 * der als einziger Knopf dauerhaft über einem Bild lag.
 *
 * Die Kennung des Videos geht dabei nicht verloren: das Menü gibt sie mit,
 * wenn genau ein Anhang an der Nachricht hängt (`einzelnesMedium` in
 * `Bubble.tsx`), und ein Video liegt praktisch immer allein.
 */
/**
 * Was der Player von einem Video braucht (#86, 1.56.0).
 *
 * Bis dahin stand hier `Media`, und das ging, solange der Player nur im Chat
 * und am Punkt stand. In der Großansicht kommt das Video aus drei Quellen
 * (siehe `Grossmedium`), und die schmalste kennt weder Bytes noch Kodierung.
 * Der Steckbrief lässt weg, was er nicht hat -- genau das tat er vorher
 * schon für eine Dauer von null.
 */
export type Videoquelle = Pick<Media, 'id' | 'url' | 'mime' | 'width' | 'height'> &
  Partial<Pick<Media, 'poster_url' | 'duration_ms' | 'bytes' | 'codec'>>

export function VideoPlayer({ media }: { media: Videoquelle }) {
  const dauer = media.duration_ms ?? 0
  const bytes = media.bytes ?? 0
  const standbild = media.poster_url ?? null
  const t = useTexte()
  const [gestartet, setGestartet] = useState(false)
  const [fehler, setFehler] = useState(false)
  const player = useRef<HTMLVideoElement>(null)
  // Die Form des Kastens kommt vom Video selbst, sobald es sie kennt.
  //
  // Vorher stand hier `width`/`height` aus der Datenbank -- also eine
  // Behauptung über die Form, die niemand geprüft hatte. Eine iPhone-Aufnahme
  // liegt in der Datei immer quer und trägt die Drehung als Marke daneben; ob
  // ein Browser die gedrehte oder die rohe Größe meldet, ist von Version zu
  // Version verschieden. Ein Querformat-Video steckte deshalb in einem
  // hochkanten Rahmen mit schwarzen Balken.
  const [form, setForm] = useState<number | null>(
    media.width > 0 && media.height > 0 ? media.width / media.height : null,
  )
  const abspielbar = kannAbspielen(media.mime)

  // Getrennt aufgesammelt statt in der Zeile zusammengesetzt: was es nicht
  // gibt, soll auch keinen Trenner hinterlassen.
  const steckbrief = [
    dauer > 0 ? dauerText(dauer) : '',
    // Die **kurze** Kante: eine Hochkantaufnahme misst 1080 x 1920, und
    // „1920p" stünde dort falsch.
    media.width > 0 && media.height > 0 ? `${Math.min(media.width, media.height)}p` : '',
    bytes > 0 ? groesse(bytes) : '',
    // **Das Format, und warum es hier steht** (#4). Aus dem Betrieb kam „bei
    // Videos auch noch das Format darstellen" -- und dahinter steckt mehr als
    // Neugier: `mime` ist bei jedem Video `video/mp4`, egal ob H.264 oder
    // HEVC drinsteckt. Wenn ein Android-Gerät ein Video nicht abspielt, ist
    // das hier die einzige Zeile, an der man sieht, woran es liegt.
    //
    // Leer bleibt es bei allem, was vor der Spalte hochgeladen wurde, und bei
    // Sprachnachrichten. Lieber nichts als geraten.
    formatName(media.codec ?? ''),
  ]
    .filter(Boolean)
    .join(' · ')

  if (!abspielbar) {
    return (
      <div className="video video--fremd">
        {standbild && <img className="video__poster" src={standbild} alt="" />}
        <p className="video__hinweis">{t.technik.video.fremdesFormat}</p>
        {/* Bewusst ein Link und kein Download-Attribut: der Browser darf
            selbst entscheiden, ob er es speichert oder in einem eigenen Tab
            versucht -- manche können es dort doch. */}
        <a className="button button--outline" href={media.url} target="_blank" rel="noreferrer">
          {t.technik.video.oeffnen(dauer > 0 ? dauerText(dauer) : '')}
        </a>
      </div>
    )
  }

  /**
   * Noch einmal von vorn: `load()` verwirft, was der Player hat, und holt die
   * Quelle neu -- samt Standbild. Der Zustand geht vorher zurück, damit die
   * Auflage weg ist, bevor der Player wieder etwas zeigt.
   */
  const nochEinmal = () => {
    setFehler(false)
    setGestartet(false)
    player.current?.load()
  }

  return (
    <div className="video">
      <video
        ref={player}
        className="video__player"
        // Ohne Standbild ein Ausweg, der ohne uns auskommt: `#t=0.1` sagt dem
        // Player, welche Stelle er zeigen soll. Er holt sich genau diesen
        // Ausschnitt über den Bereichsabruf -- und zeigt ein Bild statt einer
        // schwarzen Fläche, auch wenn das Standbild nie entstanden ist.
        src={standbild === null ? `${media.url}#t=0.1` : media.url}
        poster={standbild ?? undefined}
        controls
        // Ohne `playsInline` reisst iOS das Video in den Vollbildplayer, und
        // man ist aus dem Verlauf heraus.
        playsInline
        // **`metadata`, und hier stand `auto` -- mit einer Begründung, die
        // nicht trug.** Sie lautete: aus dem Betrieb kam „Videos ruckeln beim
        // Abspielen", und wer vorpuffern darf, spielt glatt. Das Merkmal kann
        // das aber nicht leisten: `preload` bestimmt nur, was der Player
        // **vor dem Antippen** holt. Wie weit er **während** des Abspielens
        // vorausliest, entscheidet er selbst, bei `metadata` genauso.
        //
        // Was `auto` dagegen sicher tat, kam als Meldung vom iPad (1.5.0):
        // jedes Video des Verlaufs lud sofort, drei 4K-Ströme gleichzeitig.
        // Safari holt dann Kopf und erstes Bild, blendet das Standbild aus,
        // sobald das Bild da ist -- und iPadOS nimmt den Playern, die gerade
        // nicht spielen, den Dekodierer wieder weg. Übrig blieb eine schwarze
        // Fläche ohne Standbild, und der nächste Versuch war ein Fehler.
        //
        // `metadata` holt den Kopf: die wahre Form kennt der Browser also
        // weiterhin **vor** dem Antippen, `onLoadedMetadata` kommt wie zuvor.
        preload="metadata"
        // Der gespeicherte Wert hält nur den Platz frei, damit der Verlauf
        // beim Laden nicht springt. Was gilt, sagt das Video selbst.
        style={form === null ? undefined : { aspectRatio: String(form) }}
        onLoadedMetadata={(event) => {
          const el = event.currentTarget
          if (el.videoWidth > 0 && el.videoHeight > 0) {
            setForm(el.videoWidth / el.videoHeight)
          }
        }}
        onPlay={() => setGestartet(true)}
        // **Der Fehler bekommt eine Zeile in der Diagnose, mit Code.** Daran
        // unterscheidet sich beim nächsten Mal, ob der Dekodierer weg war
        // (`MEDIA_ERR_DECODE`, 3) oder die Datei (`MEDIA_ERR_NETWORK`, 2,
        // oder `SRC_NOT_SUPPORTED`, 4). Ohne die Zahl hieße die Meldung wieder
        // nur „schwarz".
        onError={(event) => {
          const el = event.currentTarget
          const grund = el.error
          notiere(
            `Video ${media.id}: Fehler ${grund?.code ?? '?'}`
            + ` (${grund?.message || 'ohne Meldung'})`
            + ` · readyState ${el.readyState} · networkState ${el.networkState}`,
          )
          setFehler(true)
        }}
      />
      {/* **Was da liegt, bevor man tippt.** Länge, Höhe und Gewicht — die
          Angaben kommen vom Server und beschreiben die **abgelegte** Datei,
          nicht die, die jemand ausgewählt hat. Wer wissen will, ob das
          Verkleinern gewirkt hat, sieht es hier auch Tage später noch.

          Nur vor dem Abspielen: währenddessen gehört die Ecke dem Player. */}
      {!gestartet && steckbrief !== '' && <span className="video__dauer">{steckbrief}</span>}
      {/* **Bei einem Fehler das Standbild statt Schwarz.** Der Player zeigt
          nach einem Fehler nichts mehr, auch das Standbild nicht -- er hatte
          es schon gegen das erste Bild getauscht. Hier liegt es noch einmal,
          dazu ein Satz und der Weg zurück. Nach dem Steckbrief im Baum, damit
          der über der Auflage bleibt: er sagt weiterhin, was da liegt. */}
      {fehler && (
        <div className="video__fehler" data-schluessel="video-fehler">
          {standbild && <img className="video__fehlerbild" src={standbild} alt="" />}
          <p className="video__fehlertext" role="alert">{t.technik.video.abspielfehler}</p>
          <button type="button" className="button video__nochmal" onClick={nochEinmal}>
            {t.technik.video.nochEinmal}
          </button>
        </div>
      )}
    </div>
  )
}
